/// System prompts for all AI modes: chat, element explanation, BPMN generation,
/// N8N generation, and error diagnosis. Also provides buildDiagramContext()
/// to assemble the current diagram state for LLM context.

import type { EditorTab } from "@/types/editor";
import type { SelectionInfo } from "@/types/editor";

// ---------------------------------------------------------------------------
// Chat system prompt
// ---------------------------------------------------------------------------

/** General assistant prompt for conversational AI interactions. */
export const CHAT_SYSTEM_PROMPT = `You are InovaBPM AI, an assistant for the InovaBPM workflow designer. You help users design BPMN processes and node-based data flows. When the user shares their current diagram context, reference specific elements by name/ID. Be concise and practical.`;

// ---------------------------------------------------------------------------
// Element explanation prompt
// ---------------------------------------------------------------------------

/** Prompt for explaining a selected diagram element in plain language. */
export const ELEMENT_EXPLANATION_PROMPT = `The user has selected an element in their diagram and wants to understand what it does. Explain in plain, non-technical language first. Then provide an expandable technical details section with the element ID and any relevant BPMN/nodeflow specification references. The element details are provided in the context.`;

// ---------------------------------------------------------------------------
// BPMN generation system prompt
// ---------------------------------------------------------------------------

/** Full prompt for NL-to-BPMN XML generation with layout requirements. */
export const BPMN_GENERATION_SYSTEM_PROMPT = `You are InovaBPM AI, a BPMN 2.0 diagram generator. Given a natural language description, produce a valid BPMN 2.0 XML document.

Requirements for the XML output:
- XML declaration: \`<?xml version="1.0" encoding="UTF-8"?>\`
- Root element: \`<bpmn:definitions\` with namespaces:
  - xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL"
  - xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"
  - xmlns:dc="http://www.omg.org/spec/DD/20100524/DC"
  - xmlns:di="http://www.omg.org/spec/DD/20100524/DI"
- Include a \`<bpmndi:BPMNDiagram>\` with a \`<bpmndi:BPMNPlane>\` containing:
  - \`<bpmndi:BPMNShape>\` for each element with \`<dc:Bounds x="" y="" width="" height=""/>\`
  - \`<bpmndi:BPMNEdge>\` for each sequence flow with \`<di:waypoint x="" y=""/>\` entries
- Use proper element IDs (e.g., StartEvent_1, Task_1, Gateway_1, EndEvent_1)
- Connect elements with \`<bpmn:sequenceFlow>\` referencing sourceRef and targetRef
- Layout left-to-right with approximately 200px horizontal spacing and 100px vertical spacing
- Standard element sizes: events 36x36, tasks 100x80, gateways 50x50

Respond with the XML in a \`\`\`xml code block, followed by a brief description of the generated process.`;

// ---------------------------------------------------------------------------
// N8N generation system prompt
// ---------------------------------------------------------------------------

/** Full prompt for NL-to-N8N JSON generation (later converted to InovaBPM NodeFlow). */
export const N8N_GENERATION_SYSTEM_PROMPT = `You are InovaBPM AI, a workflow automation node flow generator. Given a natural language description, produce a valid N8N-compatible JSON workflow.

Requirements for the JSON output:
- Root object with "nodes" array and "connections" object
- Each node in "nodes" array must have:
  - "id": unique string identifier
  - "name": human-readable name
  - "type": valid n8n node type (e.g., "n8n-nodes-base.httpRequest", "n8n-nodes-base.if", "n8n-nodes-base.code", "n8n-nodes-base.set", "n8n-nodes-base.merge", "n8n-nodes-base.splitInBatches", "n8n-nodes-base.webhook", "n8n-nodes-base.cron", "n8n-nodes-base.function")
  - "position": [x, y] array for layout
  - "parameters": object with node-specific configuration
- "connections" object maps source node names to arrays of connection objects:
  - Each connection: { "main": [[{ "node": "target-name", "type": "main", "index": 0 }]] }
- Supported n8n node types: httpRequest, if, code, set, merge, splitInBatches, webhook, cron, function, emailSend, slack, discord, postgres, mysql, mongodb, redis, s3, ftp
- Layout left-to-right with approximately 200px horizontal spacing

Respond with the JSON in a \`\`\`json code block, followed by a brief description of the generated workflow.`;

// ---------------------------------------------------------------------------
// Error diagnosis system prompt
// ---------------------------------------------------------------------------

/** Prompt for analyzing and diagnosing workflow errors with fix suggestions. */
export const ERROR_DIAGNOSIS_SYSTEM_PROMPT = `You are diagnosing a workflow error. Analyze the error context and the current diagram. Provide: (1) A plain language explanation of the likely cause. (2) A primary recommended fix in a \`\`\`xml or \`\`\`json code block (the complete corrected diagram). (3) 1-2 alternative fixes described briefly. Start with the plain language explanation.`;

// ---------------------------------------------------------------------------
// Diagram context builder
// ---------------------------------------------------------------------------

/**
 * Build a context string describing the current diagram state for the LLM.
 * Includes the diagram content (BPMN XML or NodeFlow JSON) and any selected
 * elements with their properties.
 *
 * @param tab - The active editor tab containing diagram content and type
 * @param selection - Optional selection info with selected element IDs and properties
 * @returns Formatted context string to prepend to the user message
 */
export function buildDiagramContext(tab: EditorTab, selection?: SelectionInfo): string {
  const parts: string[] = [];

  // Diagram type header
  const diagramType = tab.editorType === "bpmn" ? "BPMN" : "Node Flow";
  parts.push(`Current diagram: ${diagramType} (${tab.title})`);

  // Diagram content
  if (tab.content) {
    const lang = tab.editorType === "bpmn" ? "xml" : "json";
    parts.push(`\n\`\`\`${lang}\n${tab.content}\n\`\`\``);
  } else {
    parts.push("\nThe diagram is currently empty.");
  }

  // Selected elements
  if (selection && selection.selectedIds.length > 0) {
    parts.push(`\nSelected elements: ${selection.selectedIds.join(", ")}`);
    if (selection.elementType) {
      parts.push(`Element type: ${selection.elementType}`);
    }
    if (selection.elementProperties) {
      parts.push(`Element properties: ${JSON.stringify(selection.elementProperties, null, 2)}`);
    }
  }

  return parts.join("\n");
}
